import { useMemo, type PropsWithChildren } from 'react';
import Markdown, { type Components } from 'react-markdown';
import { tv } from 'tailwind-variants';
import { cn } from '../utils';

const markdownTextVariants = tv({
  slots: {
    h1: 'headline1 whitespace-normal',
    h2: 'headline2 whitespace-normal',
    h3: 'headline3 whitespace-normal',
    ul: 'list-disc list-inside',
    ol: 'list-decimal list-inside',
    li: 'headline4 whitespace-normal',
    p: 'medium5 whitespace-normal',
    a: '',
  },
  variants: {
    variant: {
      default: '',
      eula: {
        p: 'text-secondary p4 whitespace-pre-line',
        a: 'headline5 text-primary',
      },
    },
    isError: {
      true: {
        p: 'text-destructive',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    isError: false,
  },
});

type Props = {
  children: string;
  variant?: 'default' | 'eula';
  isError?: boolean;
  components?: Components;
  className?: string;
};

const MarkdownText = ({
  children,
  variant = 'default',
  isError = false,
  components = {},
  className,
}: PropsWithChildren<Props>) => {
  const { h1, h2, h3, ul, ol, li, p, a } = markdownTextVariants({
    variant,
    isError,
  });

  const defaultComponents: Components = useMemo(
    () => ({
      h1: ({ ...props }) => <h1 {...props} className={h1()} />,
      h2: ({ ...props }) => <h2 {...props} className={h2()} />,
      h3: ({ ...props }) => <h3 {...props} className={h3()} />,
      ul: ({ ...props }) => <ul {...props} className={ul()} />,
      ol: ({ ...props }) => <ol {...props} className={ol()} />,
      li: ({ ...props }) => <li {...props} className={li()} />,
      p: ({ ...props }) => <p {...props} className={p()} />,
      a: ({ ...props }) => (
        <a
          {...props}
          target="_blank"
          rel="noopener noreferrer"
          className={a()}
        />
      ),
    }),
    [h1, h2, h3, ul, ol, li, p, a]
  );

  return (
    <div className={cn('flex flex-col gap-[0.5rem]', className)}>
      <Markdown components={{ ...defaultComponents, ...components }}>
        {children}
      </Markdown>
    </div>
  );
};

export { MarkdownText };
